import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Heading, Text, Flex, Spinner, Button, Badge, Switch, IconButton, useColorModeValue, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Input, FormErrorMessage,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from '@chakra-ui/react';
import { MdDragIndicator, MdDelete, MdChevronRight } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, KeyboardSensor, TouchSensor,
  useSensor, useSensors, type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchCollections, createCollection, reorderCollections, updateCollection, deleteCollection,
  collectionsQueryKey, type PlaylistCollectionDto, type CreateCollectionRequest,
} from '../../api/playlistCurationApi';
import { ApiError } from '../../services/apiClient';

const cardShadow = '0px 18px 40px rgba(112, 144, 176, 0.12)';

// ─── One sortable collection row ──────────────────────

interface CollectionRowProps {
  collection: PlaylistCollectionDto;
  onOpen: (slug: string) => void;
  onTogglePublish: (c: PlaylistCollectionDto) => void;
  onDelete: (c: PlaylistCollectionDto) => void;
  togglingSlug: string | null;
}

function CollectionRow({ collection, onOpen, onTogglePublish, onDelete, togglingSlug }: CollectionRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: collection.slug });
  const ukTitle = collection.titles?.uk;
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      align="center"
      gap={3}
      px={4}
      py={3}
      mb={2}
      borderRadius="14px"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      data-testid={`collection-row-${collection.slug}`}
    >
      <Box
        as="span"
        cursor="grab"
        color="gray.400"
        {...attributes}
        {...listeners}
        data-testid={`collection-drag-${collection.slug}`}
        aria-label={t('admin.playlistCollections.dragHandle', 'Drag to reorder')}
      >
        <MdDragIndicator size={20} />
      </Box>

      <Box flex={1} minW={0} cursor="pointer" onClick={() => onOpen(collection.slug)}>
        <Flex align="center" gap={2}>
          <Text fontWeight="600" fontSize="sm" noOfLines={1}>{collection.title}</Text>
          {ukTitle && (
            <Text fontSize="xs" color="gray.400" noOfLines={1}>/ {ukTitle}</Text>
          )}
        </Flex>
        <Text fontSize="xs" color="gray.500">
          {collection.slug} · {t('admin.playlistCollections.playlistCount', { count: collection.playlistCount, defaultValue: '{{count}} playlists' })}
        </Text>
      </Box>

      <Badge colorScheme={collection.isPublished ? 'green' : 'gray'} data-testid={`collection-status-${collection.slug}`}>
        {collection.isPublished ? t('admin.playlists.published', 'Published') : t('admin.playlists.draft', 'Draft')}
      </Badge>

      <Switch
        isChecked={collection.isPublished}
        onChange={() => onTogglePublish(collection)}
        isDisabled={togglingSlug === collection.slug}
        colorScheme="green"
        data-testid={`collection-publish-toggle-${collection.slug}`}
        aria-label={t('admin.playlistCollections.togglePublish', 'Toggle published')}
      />

      <IconButton
        size="sm"
        variant="ghost"
        colorScheme="red"
        aria-label={t('common.delete', 'Delete')}
        icon={<MdDelete />}
        onClick={() => onDelete(collection)}
        data-testid={`collection-delete-${collection.slug}`}
      />

      <IconButton
        size="sm"
        variant="ghost"
        aria-label={t('common.edit', 'Open')}
        icon={<MdChevronRight />}
        onClick={() => onOpen(collection.slug)}
        data-testid={`collection-open-${collection.slug}`}
      />
    </Flex>
  );
}

// ─── Page ─────────────────────────────────────────────

export function PlaylistCollectionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const [createOpen, setCreateOpen] = useState(false);
  // `titleUk` is a convenience field that maps to the `titles.uk` locale entry on submit.
  const [form, setForm] = useState<{ slug: string; title: string; titleUk: string }>({ slug: '', title: '', titleUk: '' });
  const [slugError, setSlugError] = useState<string | null>(null);
  const [togglingSlug, setTogglingSlug] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlaylistCollectionDto | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { data: collections, isLoading } = useQuery({
    queryKey: collectionsQueryKey,
    queryFn: fetchCollections,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const createMutation = useMutation({
    mutationFn: (body: CreateCollectionRequest) => createCollection(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKey });
      setCreateOpen(false);
      setForm({ slug: '', title: '', titleUk: '' });
      setSlugError(null);
      toast({ title: t('admin.playlistCollections.created', 'Collection created'), status: 'success', duration: 3000, isClosable: true });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        setSlugError(t('admin.playlistCollections.slugTaken', 'That slug is already in use'));
        return;
      }
      toast({ title: t('admin.playlistCollections.createFailed', 'Failed to create collection'), status: 'error', duration: 4000, isClosable: true });
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ slug, isPublished }: { slug: string; isPublished: boolean }) => updateCollection(slug, { isPublished }),
    onMutate: ({ slug }) => setTogglingSlug(slug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: collectionsQueryKey }),
    onError: () => toast({ title: t('admin.playlistCollections.updateFailed', 'Failed to update collection'), status: 'error', duration: 4000, isClosable: true }),
    onSettled: () => setTogglingSlug(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => deleteCollection(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKey });
      setPendingDelete(null);
      toast({ title: t('admin.playlistCollections.deleted', 'Collection deleted'), status: 'success', duration: 3000, isClosable: true });
    },
    onError: () => {
      setPendingDelete(null);
      toast({ title: t('admin.playlistCollections.deleteFailed', 'Failed to delete collection'), status: 'error', duration: 4000, isClosable: true });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (slugs: string[]) => reorderCollections(slugs),
    onError: () => {
      toast({ title: t('admin.playlistCollections.reorderFailed', 'Failed to reorder collections'), status: 'error', duration: 4000, isClosable: true });
      queryClient.invalidateQueries({ queryKey: collectionsQueryKey });
    },
  });

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveSlug(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !collections) return;
    const oldIndex = collections.findIndex((c) => c.slug === active.id);
    const newIndex = collections.findIndex((c) => c.slug === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(collections, oldIndex, newIndex);
    // Optimistic: write the new order to the cache before the request lands.
    queryClient.setQueryData(collectionsQueryKey, reordered);
    reorderMutation.mutate(reordered.map((c) => c.slug));
  }, [collections, queryClient, reorderMutation]);

  const handleSubmit = () => {
    setSlugError(null);
    const slug = form.slug.trim();
    const title = form.title.trim();
    if (!slug || !title) return;
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setSlugError(t('admin.playlistCollections.slugInvalid', 'Use lowercase letters, numbers, and hyphens only'));
      return;
    }
    const uk = form.titleUk.trim();
    const body: CreateCollectionRequest = { slug, title };
    if (uk) body.titles = { uk };
    createMutation.mutate(body);
  };

  const activeCollection = activeSlug ? collections?.find((c) => c.slug === activeSlug) ?? null : null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="playlist-collections-page">
      <Flex justify="space-between" align="center" mb={1}>
        <Heading size="md" color={textColor}>{t('admin.playlistCollections.title', 'Playlist collections')}</Heading>
        <Button colorScheme="brand" onClick={() => setCreateOpen(true)} data-testid="btn-create-collection">
          {t('admin.playlistCollections.create', 'New collection')}
        </Button>
      </Flex>
      <Text color={secondaryColor} fontSize="sm" mb={5}>
        {t('admin.playlistCollections.subtitle', 'Curate the home-screen carousels. Each published collection renders as one carousel in the mobile app.')}
      </Text>

      <Box borderRadius="20px" boxShadow={cardShadow} bg="white" p={4}>
        {isLoading ? (
          <Flex justify="center" p={8}><Spinner size="xl" /></Flex>
        ) : !collections || collections.length === 0 ? (
          <Box p={8} textAlign="center" data-testid="playlist-collections-empty">
            <Text color="gray.500">{t('admin.playlistCollections.empty', 'No collections yet')}</Text>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => setActiveSlug(String(e.active.id))}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveSlug(null)}
          >
            <SortableContext items={collections.map((c) => c.slug)} strategy={verticalListSortingStrategy}>
              <Box data-testid="playlist-collections-list">
                {collections.map((c) => (
                  <CollectionRow
                    key={c.slug}
                    collection={c}
                    onOpen={(slug) => navigate(`/playlist-collections/${slug}`)}
                    onTogglePublish={(col) => publishMutation.mutate({ slug: col.slug, isPublished: !col.isPublished })}
                    onDelete={(col) => setPendingDelete(col)}
                    togglingSlug={togglingSlug}
                  />
                ))}
              </Box>
            </SortableContext>
            <DragOverlay>
              {activeCollection ? (
                <CollectionRow
                  collection={activeCollection}
                  onOpen={() => {}}
                  onTogglePublish={() => {}}
                  onDelete={() => {}}
                  togglingSlug={null}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Box>

      {/* ── Create modal ── */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)}>
        <ModalOverlay />
        <ModalContent data-testid="create-collection-modal">
          <ModalHeader>{t('admin.playlistCollections.create', 'New collection')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4} isRequired>
              <FormLabel>{t('admin.playlistCollections.form.title', 'Default title')}</FormLabel>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                data-testid="create-collection-title"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>{t('admin.playlistCollections.form.titleUk', 'Ukrainian title')}</FormLabel>
              <Input
                value={form.titleUk}
                onChange={(e) => setForm((f) => ({ ...f, titleUk: e.target.value }))}
                data-testid="create-collection-titleUk"
              />
            </FormControl>
            <FormControl isRequired isInvalid={!!slugError}>
              <FormLabel>{t('admin.playlistCollections.form.slug', 'Slug')}</FormLabel>
              <Input
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="top-playlists"
                data-testid="create-collection-slug"
              />
              {slugError && <FormErrorMessage data-testid="create-collection-slug-error">{slugError}</FormErrorMessage>}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setCreateOpen(false)} data-testid="create-collection-cancel">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={createMutation.isPending}
              isDisabled={!form.slug.trim() || !form.title.trim()}
              data-testid="create-collection-submit"
            >
              {t('common.save', 'Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete confirm ── */}
      <AlertDialog isOpen={!!pendingDelete} leastDestructiveRef={cancelRef} onClose={() => setPendingDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="delete-collection-dialog">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('admin.playlistCollections.deleteTitle', 'Delete collection')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('admin.playlistCollections.deleteBody', { title: pendingDelete?.title, defaultValue: 'Delete "{{title}}"? This cannot be undone.' })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setPendingDelete(null)} data-testid="delete-collection-cancel">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.slug)}
                isLoading={deleteMutation.isPending}
                data-testid="delete-collection-confirm"
              >
                {t('common.delete', 'Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
